import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { API_URL } from './transformerlab-api-sdk';
import useSWR from 'swr';
import { deriveApiBaseUrl, getAPIFullPath, getPath } from './api-client/urls';
import {
  identifyUser,
  resetUser,
} from '../components/Shared/analytics/AnalyticsContext';
// Added types
export type Team = {
  id: string;
  name: string;
};

type UserTeamMap = Record<string, Team>;
const USER_TEAM_MAP_KEY = 'user_team_map';

let _userTeamMap: UserTeamMap = {};
if (typeof window !== 'undefined') {
  try {
    const raw = localStorage.getItem(USER_TEAM_MAP_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as unknown;
      if (parsed && typeof parsed === 'object') {
        _userTeamMap = parsed as UserTeamMap;
      }
    }
  } catch {
    _userTeamMap = {};
  }
}

function persistUserTeamMap() {
  try {
    localStorage.setItem(USER_TEAM_MAP_KEY, JSON.stringify(_userTeamMap));
  } catch {
    /* ignore */
  }
}

function getUserPreferredTeam(userId: string | undefined | null): Team | null {
  if (!userId) return null;
  const t = _userTeamMap[userId];
  if (!t || typeof t.id !== 'string' || typeof t.name !== 'string') {
    return null;
  }
  return { id: t.id, name: t.name };
}

function setUserPreferredTeam(
  userId: string | undefined | null,
  team: Team | null,
) {
  if (!userId) return;
  if (team) {
    _userTeamMap[userId] = { id: team.id, name: team.name };
  } else {
    delete _userTeamMap[userId];
  }
  persistUserTeamMap();
}

// export the AuthContextValue so consumers get correct types
export interface AuthContextValue {
  user: any;
  userError: any;
  userIsLoading: boolean;
  userMutate: any;
  login: (username: string, password: string) => Promise<void | Error>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  initializing: boolean;
  fetchWithAuth: (url: string, options?: RequestInit) => Promise<Response>;
  team: Team | null;
  setTeam: React.Dispatch<React.SetStateAction<Team | null>>;
  isDefaultPassword: boolean;
  setIsDefaultPassword: (value: boolean) => void;
}

interface AuthProviderProps {
  connection?: string;
  children: React.ReactNode;
}

// Update context generic to use null as the empty value
const AuthContext = createContext<AuthContextValue | null>(null);

// Team in-memory cache
let _currentTeam: Team | null = null;
if (typeof window !== 'undefined') {
  try {
    const raw = localStorage.getItem('current_team');
    if (raw) _currentTeam = JSON.parse(raw) as Team;
  } catch {
    _currentTeam = null;
  }
}

const listeners = new Set<() => void>();

function notifyListeners() {
  listeners.forEach((cb) => {
    try {
      cb();
    } catch (e) {
      /* ignore */
    }
  });
}

function getCurrentTeam(): Team | null {
  return _currentTeam;
}

function updateCurrentTeam(team: Team | null) {
  _currentTeam = team;
  try {
    if (team) localStorage.setItem('current_team', JSON.stringify(team));
    else localStorage.removeItem('current_team');
  } catch {
    /* ignore */
  }
  notifyListeners();
}

function logoutUser() {
  updateCurrentTeam(null);
}

// allow components to re-render when auth changes
function subscribeAuthChange(cb: () => void) {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

// --- 2. Refresh Logic (Singleton Pattern) ---

// We use a promise variable to ensure we only run ONE refresh request at a time,
// even if 5 API calls fail simultaneously.
let refreshPromise: Promise<void> | null = null;

async function handleRefresh(): Promise<void> {
  // If a refresh is already in progress, return the existing promise
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      const url = getAPIFullPath('auth', ['refresh'], {});

      // Cookie-based refresh: cookies are sent/set automatically with credentials: 'include'
      const refreshResponse = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!refreshResponse.ok) {
        // If refresh fails (e.g. 401), the refresh token cookie is invalid.
        console.error(
          '[REFRESH] Refresh failed with status:',
          refreshResponse.status,
        );
        const error: any = new Error('Refresh failed');
        // Mark this specifically as an auth failure so callers can distinguish it
        error.status = 'unauthorized';
        throw error;
      }

      // Cookies are refreshed by the server response, nothing to store locally
    } catch (error) {
      console.error('[REFRESH] Token refresh failed. Logging out.', error);
      logoutUser();
      throw error;
    } finally {
      // Reset the promise so future failures trigger a new refresh
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// --- 3. Wrapper Fetch Function ---
export async function fetchWithAuth(url: string, options: RequestInit = {}) {
  const currentTeam = getCurrentTeam();

  // Handle cases where url might be partial or full
  // Ideally fetchWithAuth is passed a relative path, but we handle both.
  let fullUrl: string;
  if (url.startsWith('http') || url.startsWith('//') || url.startsWith('/')) {
    // Already an absolute URL (http://...) or absolute path (/lab/...).
    // getAPIFullPath returns the latter when API_URL is configured as a path
    // prefix (e.g. when the app is hosted behind a reverse proxy at /lab/).
    fullUrl = url;
  } else {
    const baseUrl = API_URL() ?? deriveApiBaseUrl();
    // baseUrl always has a trailing slash
    fullUrl = `${baseUrl}${url}`;
  }

  const method = (options.method ?? 'GET').toUpperCase();
  const isHealthCheck = fullUrl.endsWith('/healthz');

  // Important: avoid forcing CORS preflight on GET/HEAD (especially health checks)
  // by not attaching non-simple headers unless needed.
  const headers: Record<string, string> = {
    ...((options.headers as Record<string, string>) || {}),
    // Only set Content-Type when we actually have a non-FormData body.
    ...(options.body && !(options.body instanceof FormData)
      ? { 'Content-Type': 'application/json' }
      : {}),
    // Only attach team headers when not doing lightweight health checks.
    ...(currentTeam && !isHealthCheck
      ? { 'X-Team-Id': currentTeam.id, 'X-Team-Name': currentTeam.name }
      : {}),
  };

  // Cookie-based auth: credentials: 'include' sends cookies automatically
  const response = await fetch(fullUrl, {
    ...options,
    method,
    headers,
    credentials: 'include',
  });

  // If Unauthorized (401)
  if (response.status === 401) {
    try {
      // Attempt to refresh token via cookie-based refresh
      await handleRefresh();

      // Retry the original request (cookies are refreshed automatically)
      return fetch(fullUrl, {
        ...options,
        headers,
        credentials: 'include',
      });
    } catch (e) {
      // Refresh failed (and user was logged out inside handleRefresh)
      console.error('[FETCH_WITH_AUTH] Refresh failed, throwing error:', e);
      throw e;
    }
  }

  return response;
}

export function AuthProvider({ connection, children }: AuthProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [initializing, setInitializing] = useState<boolean>(true);
  const [team, setTeamState] = useState<Team | null>(null);
  const [isDefaultPassword, setIsDefaultPassword] = useState(false);

  useEffect(() => {
    // Initialize team from localStorage
    setTeamState(getCurrentTeam());
    setInitializing(false);

    // Subscribe to team changes
    const unsub = subscribeAuthChange(() => {
      setTeamState(getCurrentTeam());
    });
    return unsub;
  }, []);

  // Fetch user data using cookie-based auth; presence of a valid user implies authentication
  const userKey = getAPIFullPath('users', ['me'], {}) ?? null;
  const {
    data: user,
    error: userError,
    isLoading: userIsLoading,
    mutate: userMutate,
  } = useSWR(userKey, (url) => fetchWithAuth(url).then((r) => r.json()));

  // Once we know the user result, derive isAuthenticated from it.
  // Only clear auth on 401 (unauthorized); network/connection errors keep the user
  // "authenticated" so the app can show Connection Lost modal instead of LoginPage.
  useEffect(() => {
    if (user && !userError) {
      if (!isAuthenticated) {
        setIsAuthenticated(true);
      }
    } else if (userError) {
      const status = (userError as any).status;
      // Only treat explicit auth failures as a signal to log the user out.
      // Network / connectivity errors should NOT flip isAuthenticated to false,
      // so the UI can continue to show the main app + connection lost modal.
      if ((status === 401 || status === 'unauthorized') && isAuthenticated) {
        setIsAuthenticated(false);
      }
    }
  }, [user, userError, isAuthenticated]);

  // Get a list of teams this user belongs to (only after user is known)
  const teamsKey =
    user && !userError ? getAPIFullPath('teams', ['list'], {}) : null;
  const { data: teamsData, mutate: teamsMutate } = useSWR(teamsKey, (url) =>
    fetchWithAuth(url).then((r) => r.json()),
  );

  useEffect(() => {
    // Logic to auto-select team on login/user change, prioritizing per-user preference only
    const teams = teamsData?.teams as Team[] | undefined;
    const userId = user?.id as string | undefined;

    if (teams && teams.length > 0 && userId) {
      const preferred = getUserPreferredTeam(userId);
      const hadPreferred = !!preferred;

      // 1. If preferred is still in this user's teams, use it.
      // 2. Otherwise, fall back to the first available team.
      let desired: Team | null = null;
      let usedPreferred = false;

      if (preferred) {
        const preferredInList = teams.find((t) => t.id === preferred.id);
        if (preferredInList) {
          desired = { id: preferredInList.id, name: preferredInList.name };
          usedPreferred = true;
        }
      }

      if (!desired) {
        const first = teams[0];
        desired = { id: first.id, name: first.name };
      }

      // Only update state/caches if we actually need to change
      if (!team || team.id !== desired.id || team.name !== desired.name) {
        updateCurrentTeam(desired);
        setTeamState(desired);
      }

      // Only update the stored preference when:
      // - we successfully used an existing preferred team, or
      // - the user had no prior preference (first login) and we're initializing it.
      // This avoids overwriting an explicit user choice with a fallback (team[0]).
      if (usedPreferred || !hadPreferred) {
        setUserPreferredTeam(userId, desired);
      }
      document.cookie = `tlab_team_id=${desired.id}; path=/; SameSite=Lax`;
    } else if (teams && teams.length === 0) {
      // No teams available for this user
      updateCurrentTeam(null);
      setTeamState(null);
      setUserPreferredTeam(userId, null);
      document.cookie = 'tlab_team_id=; Max-Age=0; path=/; SameSite=Lax';
    }
  }, [teamsData, user, team]);

  // Identify user in analytics when user data is available
  useEffect(() => {
    if (user?.id) {
      identifyUser(user.id, {
        email: user.email,
      });
    }
  }, [user]);

  useEffect(() => {
    setIsDefaultPassword(Boolean(user?.is_default_password));
  }, [user?.is_default_password]);

  // Login handler
  const handleLogin = useCallback(
    async (username: string, password: string) => {
      try {
        const form = new URLSearchParams({
          username: username,
          password: password,
        }).toString();

        // Cookie-based login: credentials: 'include' allows cookies to be set
        const res = await fetch(getAPIFullPath('auth', ['login'], {}), {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: form,
          credentials: 'include',
        });

        const data = await (async () => {
          try {
            return await res.json();
          } catch {
            return {};
          }
        })();

        if (!res.ok) {
          console.error(`Login failed: ${res.status} ${JSON.stringify(data)}`);
          const error = new Error(
            `Login failed: ${res.status} ${JSON.stringify(data)}`,
          );
          (error as any).info = data;
          (error as any).status = res.status;
          return error;
        }

        // Login successful - cookies are set by the server
        // Set authenticated state to trigger user/teams data fetch
        setIsAuthenticated(true);

        // Revalidate user/teams data
        if (userMutate) userMutate();
        if (teamsMutate) teamsMutate();
      } catch (e) {
        console.error(
          `Login error: ${e instanceof Error ? e.message : String(e)}`,
        );
        return e instanceof Error ? e : new Error(String(e));
      }
    },
    [userMutate, teamsMutate],
  );

  // // Register handler
  // const handleRegister = useCallback(async () => {
  //   try {
  //     const body = {
  //       email: 'test@example.com',
  //       password: 'password123',
  //     };
  //     const res = await fetchWithAuth('auth/register', {
  //       method: 'POST',
  //       headers: { 'Content-Type': 'application/json' },
  //       body: JSON.stringify(body),
  //     });

  //     // Note: Register usually doesn't return tokens in fastapi-users default flow,
  //     // but if it does, we handle it.
  //     if (!res.ok) return;

  //     const data = await res.json();
  //     const newToken = data.access_token;
  //      if (newToken) {
  //       updateAccessToken(newToken);
  //       setToken(newToken);
  //       if (userMutate) userMutate();
  //     }
  //   } catch (e) {
  //     console.error(`Register error: ${e instanceof Error ? e.message : String(e)}`);
  //   }
  // }, [userMutate]);

  // Logout handler
  const handleLogout = useCallback(async () => {
    try {
      // Cookie-based logout: clears auth cookies on server
      await fetch(
        getAPIFullPath('auth', ['logout'], {}) || '/auth/cookie/logout',
        {
          method: 'POST',
          credentials: 'include',
        },
      );
    } catch {
      /* ignore errors */
    }
    // Reset hash to root and clear any stored redirect so the next user
    // doesn't land on the previous user's deep route. Session-expiry logout
    // doesn't go through this handler, so its "return-to-route" UX is preserved.
    localStorage.removeItem('redirectAfterLogin');
    window.location.hash = '/';
    logoutUser();
    resetUser();
    setIsAuthenticated(false);
    setIsDefaultPassword(false);
    setTeamState(null);
    // Clear team cookie
    document.cookie = 'tlab_team_id=; Max-Age=0; path=/; SameSite=Lax';
    if (userMutate) userMutate(null, false);
  }, [userMutate]);

  const handleSetTeam = useCallback(
    (value: React.SetStateAction<Team | null>) => {
      const next =
        typeof value === 'function'
          ? (value as (prev: Team | null) => Team | null)(team)
          : value;
      updateCurrentTeam(next);
      setTeamState(next);
      // Persist per-user team preference so we can restore it next login
      setUserPreferredTeam(user?.id as string | undefined, next);
      // Persist team selection in cookie so non-fetch requests (e.g. <img>) have team context
      if (next?.id) {
        document.cookie = `tlab_team_id=${next.id}; path=/; SameSite=Lax`;
      } else {
        document.cookie = 'tlab_team_id=; Max-Age=0; path=/; SameSite=Lax';
      }
      // If the team changes, we reload the app to ensure all components pick up new team context
      // But only do this if the team actually changed
      if (next?.id !== team?.id) {
        window.location.reload();
      }
    },
    [team, user],
  );

  const contextValue = useMemo(
    () => ({
      user,
      userError,
      userIsLoading,
      userMutate,
      login: handleLogin,
      logout: handleLogout,
      isAuthenticated,
      initializing,
      fetchWithAuth,
      team,
      setTeam: handleSetTeam,
      isDefaultPassword,
      setIsDefaultPassword,
    }),
    [
      user,
      userError,
      userIsLoading,
      userMutate,
      handleLogin,
      handleLogout,
      isAuthenticated,
      initializing,
      team,
      handleSetTeam,
      isDefaultPassword,
      setIsDefaultPassword,
    ],
  );

  return React.createElement(
    AuthContext.Provider,
    { value: contextValue },
    children,
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
}

// Helper hook for components
export function useAPI(
  majorEntity: string,
  pathArray: string[],
  params: Record<string, any> = {},
  options: any = {},
) {
  let path: string | null = getPath(majorEntity, pathArray, params) as any;

  const fetcher = async (url: string) => {
    // Use fetchWithAuth which handles the token injection and 401 refresh logic
    const res = await fetchWithAuth(url);

    if (res.status === 401) {
      // Should have been handled by fetchWithAuth, but if still 401, return error
      return { status: 'unauthorized', message: 'User not authorized' };
    }

    if (!res.ok) {
      return { status: 'error', message: 'API returned HTTP ' + res.status };
    }

    return res.json();
  };

  if (
    Object.values(params).some((param) => param === null || param === undefined)
  ) {
    path = null;
  }

  const { data, error, isLoading, mutate } = useSWR(path, fetcher, {
    ...options,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  return { data, error, isLoading, mutate };
}

// Create a new function called useSWRWithAuth which is EXACTLY the same as useSWR,
// but uses fetchWithAuth as the fetcher.
export function useSWRWithAuth<T = any>(
  key: string | null,
  fetcher_unused?: any,
  options?: any,
) {
  const fetcher = async (url: string) => {
    const res = await fetchWithAuth(url);

    if (res.status === 401) {
      // Should have been handled by fetchWithAuth, but if still 401, return error
      const error: any = new Error('User not authorized');
      error.status = 'unauthorized';
      throw error;
    }

    if (!res.ok) {
      const error: any = new Error('API returned HTTP ' + res.status);
      error.status = res.status;
      throw error;
    }

    return res.json();
  };
  const { data, error, isLoading, isValidating, mutate } = useSWR(
    key,
    fetcher,
    options,
  );

  return {
    data: data as T,
    isLoading,
    isValidating,
    isError: error,
    mutate,
  };
}
